import { TabDescriptor } from '../types';
import { ConsultationActions, OffreActions } from './operationActions';

// Logique PURE du CTA "onglet suivant" acheteur-BC : ou va-t-on, sous quel libelle, et
// faut-il l'afficher. Aucune dependance React : meme partage des responsabilites que
// domain/operationActions — le domaine decide VISIBLE + libelle, la feuille rend.
//
// POURQUOI une resolution par CLE, et non par kind ni par `activeIndex + 1` :
//   - Les kinds 'consultation' et 'offre' sont DUPLIQUES par rang : une resolution par kind seul
//     retomberait toujours sur le rang 1. La destination est donc nommee par CLE (`offre-2`),
//     seule identite stable d'un onglet.
//   - `activeIndex + 1` suivrait l'ordre de la barre sans comprendre la cascade. La destination
//     est DERIVEE des descripteurs reels, jamais figee : depuis Offre Rang N, le suivant est la
//     Consultation du rang de repli s'il en existe un, et seulement sinon la Notification. Une
//     table figee "offre -> notification" emmenait l'acheteur au bon de commande signe alors qu'il
//     lui restait des rangs a consulter.

export interface NextTabInputs {
  // La barre d'actions de l'onglet propose deja une action orange qui fait avancer le workflow
  // (lancer, accepter, signer, obtenir la validation...). C'est ELLE le CTA : la navigation
  // n'a pas lieu d'etre. Un seul CTA orange par onglet.
  hasWorkflowAction?: boolean;
}

export interface NextTabAction {
  visible: boolean;
  label: string;
  index: number;
}

// Quels oranges de la BARRE D'ACTIONS font avancer le workflow — donc evincent la navigation.
//
// POURQUOI ici et pas dans le JSX qui les rend : c'est une derivation PURE de ConsultationActions /
// OffreActions, pas un etat local de feuille (contrairement a `chronoExpired` ou `articlesMode`, qui
// eux restent chez le composant et alimentent `computeXActions`). Laissee a cote du JSX, la
// connaissance « ces 4 actions-la sont les oranges de workflow » diverge silencieusement le jour ou
// un `variant` change dans le rendu.
//
// Ne comptent PAS : les oranges HORS barre d'actions (le « Charger » du code chiffrage vit dans le
// contenu), le telechargement du bon de commande signe (il n'avance rien) et les boutons de modale.
export const hasConsultationWorkflowAction = (actions: ConsultationActions): boolean =>
  actions.lancerConsultation.visible ||
  actions.demanderValidation.visible ||
  actions.accepterOffre.visible ||
  actions.consulterRangSuivant.visible;

export const hasOffreWorkflowAction = (actions: OffreActions): boolean =>
  actions.accepterOffre.visible || actions.demanderValidation.visible;

// Ce que recoit une feuille : la decision du domaine + la bascule deja reliee. `visible` lui sert
// AUSSI a decider d'afficher sa barre d'actions — une barre vide ne doit pas apparaitre pour ce
// seul bouton, ni disparaitre en l'emportant. `onClick` reste un callback nu : le domaine ignore React.
export interface NextTabRender extends NextTabAction {
  onClick: () => void;
}

const HIDDEN: NextTabAction = { visible: false, label: '', index: -1 };

// Cle de la destination metier de chaque onglet. Historique (premier) et Evaluation (dernier)
// n'ont pas de suite : ils retournent null.
const nextKeyOf = (tab: TabDescriptor, descriptors: TabDescriptor[]): string | null => {
  switch (tab.kind) {
    case 'consultation':
      // Offre du MEME rang : c'est la reponse du prestataire a CETTE consultation-la.
      return tab.rang ? `offre-${tab.rang}` : null;
    case 'offre': {
      // La cascade AVANT le bon de commande : depuis "Offre Rang 1", s'il existe un
      // "Consultation Rang 2", c'est LUI le suivant — pas la notification. Sauter par-dessus les
      // rangs restants emmenerait l'acheteur au document signe alors qu'il lui reste des rangs a
      // consulter.
      // On CHERCHE le rang suivant dans les descripteurs plutot que de supposer `rang + 1` : les
      // rangs visibles sont calcules (computeVisibleRangs) et peuvent etre discontinus.
      const consultationSuivante = descriptors.find(
        (d) => d.kind === 'consultation' && (d.rang ?? 0) > (tab.rang ?? 0),
      );
      return consultationSuivante ? consultationSuivante.key : 'notification';
    }
    case 'notification':
      return 'messagerie';
    case 'messagerie':
      return 'evaluation';
    default:
      return null;
  }
};

const labelOf = (target: TabDescriptor): string => {
  switch (target.kind) {
    case 'consultation':
      // "Voir" et non "Consulter" : le distinguer du bouton d'ACTION "Consulter le prestataire de
      // rang N", qui lance la consultation. Ici on ne fait que naviguer.
      return `Voir la consultation Rang ${target.rang}`;
    case 'offre':
      return `Voir l'offre Rang ${target.rang}`;
    case 'notification':
      return 'Accéder au bon de commande signé';
    case 'messagerie':
      return 'Aller dans la messagerie';
    case 'evaluation':
      return 'Évaluer le prestataire';
    default:
      return '';
  }
};

export const computeNextTabAction = (
  descriptors: TabDescriptor[],
  activeTab: TabDescriptor | null,
  inputs: NextTabInputs = {},
): NextTabAction => {
  if (!activeTab || inputs.hasWorkflowAction) return HIDDEN;

  const nextKey = nextKeyOf(activeTab, descriptors);
  if (!nextKey) return HIDDEN;

  const index = descriptors.findIndex((d) => d.key === nextKey);
  const target = descriptors[index];
  // Destination grisee : useOperationTabs.setActiveIndex la refuse SILENCIEUSEMENT. Sans cette
  // garde, le bouton existerait et ne ferait rien.
  if (!target || target.disabled || target.inactive) return HIDDEN;

  const label = labelOf(target);
  if (!label) return HIDDEN;

  return { visible: true, label, index };
};
